import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  SlidersHorizontal,
  RefreshCw,
  Loader2,
  CloudOff,
  ChevronLeft,
  ChevronRight,
  Bookmark,
  Sparkles,
  Clock,
  Rss
} from "lucide-react";
import useJobStore from "../store/jobStore";
import useAuthStore from "../store/authStore";
import {
  getSyncStatus,
  syncJobsNow,
  getUpworkUserSummary,
} from "../services/tenantService";
import useFilterStore from "../store/filterStore";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { usePermissions } from "../hooks/usePermissions";
import { getSavedJobs } from "../services/savedJobsService";
import { getProposalByJobId, getProposalCounts } from "../services/proposalService";
import { markNotInterested, removeNotInterested, getNotInterestedJobs } from "../services/notInterestedService";
import configs from "../config";
import { trackEvent } from '../contexts/PostHogProvider';

// Import new components
import JobCard from "./JobCard";
import JobBoardLayout from "./JobBoardLayout";
import JobFilters from "./JobFilters";
import JobFiltersModal from "./JobFiltersModal";
import JobAdvancedSearchModal from "./JobAdvancedSearchModal";
import SavedSearches from "./SavedSearches";
import ProfileCompletionCard from "./ProfileCompletionCard";
import QuickStatsPanel from "./QuickStatsPanel";
import HelpLinksPanel from "./HelpLinksPanel";
import { JobDetailsModal } from "./JobDetailsModal";
import { SyncStatusBar } from "./SyncStatusBar";
import Toast from "./Toast";

// Tab options
const TABS = [
  { id: 'best', label: 'Best Matches', icon: Sparkles },
  { id: 'recent', label: 'Most Recent', icon: Clock },
  { id: 'feed', label: 'My Feed', icon: Rss },
  { id: 'saved', label: 'Saved Jobs', icon: Bookmark },
];

export function JobBoard() {
  const { logout, isAuthenticated } = useAuthStore();
  const { canUpdateJobFilters } = usePermissions();
  const { jobs, setJobs, removeJob, addJob } = useJobStore();
  const { filters, activeFilterCount, buildQueryParams, hasHydrated: filtersHydrated } = useFilterStore();
  const navigate = useNavigate();

  useDocumentTitle('Find Work - Propel');

  // State
  const [activeTab, setActiveTab] = useState('best');
  const [searchTerm, setSearchTerm] = useState("");
  const [advancedSearch, setAdvancedSearch] = useState({
    allWords: '',
    anyWords: '',
    noneWords: '',
    exactPhrase: '',
    titleSearch: '',
    skillsSearch: '',
  });
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobIndex, setSelectedJobIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proposalStatuses, setProposalStatuses] = useState({});
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [savedJobs, setSavedJobs] = useState([]);
  const [savedJobsCount, setSavedJobsCount] = useState(0);
  const [toast, setToast] = useState(null);
  const [syncInfo, setSyncInfo] = useState(null);
  const [triggeringSyncManually, setTriggeringSyncManually] = useState(false);
  const [upworkSummary, setUpworkSummary] = useState(null);
  const notInterestedRef = useRef(null);
  const rowsPerPage = 20;
  const canManualSyncJobs = canUpdateJobFilters();

  // Auth check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Refresh saved jobs list and count
  const refreshSavedJobs = async () => {
    try {
      const response = await getSavedJobs();
      const savedData = response?.data || response || [];
      setSavedJobsCount(Array.isArray(savedData) ? savedData.length : 0);
      setSavedJobs(Array.isArray(savedData) ? savedData : []);
    } catch (err) {
      console.error('Failed to fetch saved jobs:', err);
      setSavedJobs([]);
      setSavedJobsCount(0);
    }
  };

  // Fetch saved jobs on mount
  useEffect(() => {
    if (isAuthenticated) {
      refreshSavedJobs();
    }
  }, [isAuthenticated]);

  // Fetch sync status for contextual empty states
  useEffect(() => {
    if (isAuthenticated) {
      getSyncStatus()
        .then((res) => {
          if (res?.success && res?.data) setSyncInfo(res.data);
        })
        .catch(() => {});
    }
  }, [isAuthenticated]);

  // Fetch Upwork profile/connect summary for sidebar cards
  useEffect(() => {
    if (!isAuthenticated) return;

    getUpworkUserSummary()
      .then((res) => {
        if (res?.success) {
          setUpworkSummary(res.data || null);
        }
      })
      .catch(() => {
        setUpworkSummary(null);
      });
  }, [isAuthenticated]);

  // Manual sync trigger from empty state
  const handleManualSync = async () => {
    if (!canManualSyncJobs) {
      setToast({ type: 'error', message: 'You do not have permission to trigger manual sync.' });
      return;
    }

    setTriggeringSyncManually(true);
    try {
      await syncJobsNow({ immediate: true });
      setToast({ type: 'success', message: 'Sync triggered — jobs will appear shortly.' });
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to trigger sync' });
    } finally {
      setTriggeringSyncManually(false);
    }
  };

  // Proposal counts for QuickStatsPanel
  const [proposalCounts, setProposalCounts] = useState(null);
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await getProposalCounts();
        if (response?.success) {
          setProposalCounts(response.counts);
        }
      } catch (err) {
        // Non-critical, fail silently
      }
    };
    if (isAuthenticated) fetchCounts();
  }, [isAuthenticated]);

  // Track current fetch to cancel stale requests
  const fetchIdRef = useRef(0);

  // Refs for tracking state changes (single effect approach)
  const prevValuesRef = useRef({
    activeTab,
    searchTerm: '',
    filtersJson: JSON.stringify(filters),
    advancedSearchJson: JSON.stringify(advancedSearch),
  });
  const debounceTimeoutRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  // Fetch jobs function - defined inline to always have fresh values
  const fetchJobs = async (page = 1, isRefresh = false) => {
    if (!isAuthenticated) {
      return;
    }

    const currentFetchId = ++fetchIdRef.current;

    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const { getTenantHeaders } = await import("../utils/apiClient");

      const params = new URLSearchParams({
        page: page.toString(),
        limit: rowsPerPage.toString(),
        sortBy: 'publishedDateTime',
        sortOrder: 'desc',
        excludeNotInterested: 'true', // Always exclude not interested jobs
      });

      // Add feed type for different sorting
      if (activeTab === 'best' || activeTab === 'recent' || activeTab === 'feed') {
        params.append('feedType', activeTab);
      }

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      const appendSearchParam = (key, value) => {
        if (value && value.trim()) {
          params.append(key, value.trim());
        }
      };

      appendSearchParam('searchAll', advancedSearch.allWords);
      appendSearchParam('searchAny', advancedSearch.anyWords);
      appendSearchParam('searchNone', advancedSearch.noneWords);
      appendSearchParam('searchExact', advancedSearch.exactPhrase);
      appendSearchParam('searchTitle', advancedSearch.titleSearch);
      appendSearchParam('searchSkills', advancedSearch.skillsSearch);

      // Add filter params
      const filterParams = buildQueryParams();
      for (const [key, value] of filterParams.entries()) {
        params.append(key, value);
      }

      const url = `${configs.BACKEND_URL}/api/jobPosting/getAll-jobPosting?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...getTenantHeaders(),
        },
      });

      // If a newer fetch started, ignore this response
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      if (response.status === 401) {
        await logout();
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch jobs");
      }

      const data = await response.json();

      // Double-check we're still the latest fetch
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      if (data.success && data.data) {
        setJobs(Array.isArray(data.data) ? data.data : []);
        setTotalPages(data.pagination?.pages || 1);
        setTotalJobs(data.pagination?.total || 0);
        setCurrentPage(page);
      } else {
        setJobs([]);
        setTotalPages(1);
        setTotalJobs(0);
      }

      setLastRefreshTime(new Date());
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      setError(err.message || "Failed to fetch jobs");
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  // Serialize filters for stable comparison
  const filtersJson = JSON.stringify(filters);
  const advancedSearchJson = JSON.stringify(advancedSearch);
  const advancedSearchCount = Object.values(advancedSearch).filter((value) => value.trim()).length;
  const hasAdvancedSearch = advancedSearchCount > 0;

  // Single consolidated effect for all fetch triggers
  useEffect(() => {
    if (!isAuthenticated || !filtersHydrated || activeTab === 'saved') {
      prevValuesRef.current = { activeTab, searchTerm, filtersJson, advancedSearchJson };
      return;
    }

    // Clear any pending debounced fetch
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    const prev = prevValuesRef.current;

    // First render - fetch immediately
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevValuesRef.current = { activeTab, searchTerm, filtersJson, advancedSearchJson };
      fetchJobs(1);
      return;
    }

    // Determine what changed
    const tabChanged = prev.activeTab !== activeTab;
    const searchChanged = prev.searchTerm !== searchTerm;
    const filtersChanged = prev.filtersJson !== filtersJson;
    const advancedChanged = prev.advancedSearchJson !== advancedSearchJson;

    // Update refs
    prevValuesRef.current = { activeTab, searchTerm, filtersJson, advancedSearchJson };

    // Nothing changed - don't fetch
    if (!tabChanged && !searchChanged && !filtersChanged && !advancedChanged) {
      return;
    }

    // Tab changed - fetch immediately
    if (tabChanged) {
      fetchJobs(1);
      return;
    }

    // Search or filter changed - debounce
    const delay = (searchChanged || advancedChanged) ? 500 : 100;
    debounceTimeoutRef.current = setTimeout(() => {
      if (searchChanged && searchTerm.trim()) {
        trackEvent('job_search_performed', { search_term: searchTerm.trim(), active_tab: activeTab });
      }
      if (advancedChanged && hasAdvancedSearch) {
        trackEvent('job_advanced_search_applied', { fields: advancedSearchCount, active_tab: activeTab });
      }
      fetchJobs(1);
    }, delay);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, filtersHydrated, activeTab, searchTerm, filtersJson, advancedSearchJson, hasAdvancedSearch, advancedSearchCount]);

  // Fetch when page changes
  const handlePageChange = (newPage) => {
    fetchJobs(newPage);
  };

  // Handle job click
  const handleJobClick = async (job, index) => {
    setSelectedJob(job);
    setSelectedJobIndex(index);
    try {
      const proposalResponse = await getProposalByJobId(job._id);
      if (proposalResponse && proposalResponse.success) {
        setProposalStatuses(prev => ({
          ...prev,
          [job._id]: proposalResponse.data
        }));
      }
    } catch (error) {
      // No proposal found
    }
  };

  // Navigate to next/previous job in modal
  const handleNavigateJob = (direction) => {
    const newIndex = selectedJobIndex + direction;
    if (newIndex >= 0 && newIndex < displayJobs.length) {
      const newJob = displayJobs[newIndex];
      handleJobClick(newJob, newIndex);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    if (activeTab === 'saved') {
      refreshSavedJobs();
    } else {
      fetchJobs(currentPage, true);
    }
  };

  // Handle not interested (optimistic removal with undo)
  const handleNotInterested = (job, reason) => {
    // Cancel any pending not-interested action
    if (notInterestedRef.current) {
      clearTimeout(notInterestedRef.current.timeout);
    }

    // Optimistically remove from UI
    removeJob(job._id);
    setTotalJobs(prev => Math.max(0, prev - 1));

    // Set up delayed API call
    const timeout = setTimeout(async () => {
      try {
        await markNotInterested(job._id, reason);
      } catch (err) {
        // If API call fails, restore the job
        addJob(job);
        setTotalJobs(prev => prev + 1);
      }
      notInterestedRef.current = null;
      setToast(null);
    }, 5000);

    notInterestedRef.current = { job, reason, timeout };

    // Show undo toast
    setToast({
      message: 'Job marked as not interested',
      actionLabel: 'Undo',
      onAction: () => {
        if (notInterestedRef.current) {
          clearTimeout(notInterestedRef.current.timeout);
          // Restore the job to the list
          addJob(notInterestedRef.current.job);
          setTotalJobs(prev => prev + 1);
          notInterestedRef.current = null;
        }
        setToast(null);
      },
      onDismiss: () => {
        // Toast dismissed — API call already scheduled via timeout
        setToast(null);
      },
    });
  };

  // Get jobs to display based on active tab
  const displayJobs = activeTab === 'saved'
    ? savedJobs.map(s => s.jobId).filter(Boolean)
    : (Array.isArray(jobs) ? jobs : []);

  // Create a Set of saved job IDs for quick lookup (prevents individual API calls in JobCard)
  const savedJobIds = new Set(savedJobs.map(s => s.jobId?._id || s.jobId).filter(Boolean));

  if (!isAuthenticated) {
    return null;
  }

  // Left sidebar content - Filters
  const leftSidebar = <JobFilters />;

  // Right sidebar content - Profile & Stats
  const rightSidebar = (
    <>
      <ProfileCompletionCard upworkSummary={upworkSummary} />
      <QuickStatsPanel
        proposalsSent={proposalCounts?.active}
        connects={upworkSummary?.connects}
        connectsStatus={upworkSummary?.connectsStatus}
        connectsMessage={upworkSummary?.connectsMessage}
        upworkConnected={upworkSummary?.connected}
      />
      <HelpLinksPanel />
    </>
  );

  return (
    <JobBoardLayout leftSidebar={leftSidebar} rightSidebar={rightSidebar}>
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for jobs..."
                data-onboarding="jobs-search"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowFiltersModal(true)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                activeFilterCount > 0
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-primary-600 text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowAdvancedSearch(true)}
              className="text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              Advanced search
            </button>
            {hasAdvancedSearch && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-primary-50 text-primary-700 border border-primary-100">
                  Advanced: {advancedSearchCount}
                </span>
                <button
                  type="button"
                  onClick={() => setAdvancedSearch({
                    allWords: '',
                    anyWords: '',
                    noneWords: '',
                    exactPhrase: '',
                    titleSearch: '',
                    skillsSearch: '',
                  })}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sync Status Bar */}
        <SyncStatusBar />

        {/* Saved Searches */}
        <SavedSearches />

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const count = tab.id === 'saved' ? savedJobsCount : null;

                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); trackEvent('job_board_tab_changed', { tab: tab.id }); }}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      isActive
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                    {count !== null && (
                      <span className={`ml-1 px-1.5 py-0.5 text-xs font-medium rounded-full ${
                        isActive ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content Header */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {activeTab === 'saved'
                  ? `${savedJobsCount} saved jobs`
                  : `${totalJobs.toLocaleString()} jobs found`
                }
              </span>
              {lastRefreshTime && activeTab !== 'saved' && (
                <span className="text-xs text-gray-400">
                  Updated {lastRefreshTime.toLocaleTimeString()}
                </span>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Job List */}
          <div className="p-4">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-100 rounded-lg h-48" />
                  </div>
                ))}
              </div>
            ) : displayJobs.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  {activeTab === 'saved' ? (
                    <Bookmark className="w-8 h-8 text-gray-400" />
                  ) : activeFilterCount === 0 ? (
                    <SlidersHorizontal className="w-8 h-8 text-gray-400" />
                  ) : activeFilterCount > 0 && !syncInfo?.jobs?.lastSyncTime ? (
                    <CloudOff className="w-8 h-8 text-gray-400" />
                  ) : (
                    <Search className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {activeTab === 'saved'
                    ? 'No saved jobs'
                    : activeFilterCount === 0
                    ? 'No job filters configured'
                    : activeFilterCount > 0 && !syncInfo?.jobs?.lastSyncTime
                    ? 'No jobs yet'
                    : 'No jobs found'
                  }
                </h3>
                <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
                  {activeTab === 'saved'
                    ? 'Save jobs you\'re interested in to review them later'
                    : activeFilterCount === 0
                    ? 'Get started by configuring your job filters. This helps us find relevant jobs from Upwork that match your skills and preferences.'
                    : activeFilterCount > 0 && !syncInfo?.jobs?.lastSyncTime
                    ? canManualSyncJobs
                      ? 'Your filters are set up! Jobs will sync automatically from Upwork, or you can trigger a sync now to get started right away.'
                      : 'Your filters are set up! Jobs will sync automatically from Upwork shortly.'
                    : searchTerm || hasAdvancedSearch
                    ? 'No jobs match your search criteria. Try adjusting your search terms or filters.'
                    : 'No jobs match your current filters. Try adjusting your filter settings to see more opportunities.'
                  }
                </p>
                {activeTab === 'saved' ? (
                  <button
                    onClick={() => setActiveTab('best')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Search className="w-4 h-4" />
                    Browse Jobs
                  </button>
                ) : activeFilterCount === 0 ? (
                  <button
                    onClick={() => setShowFiltersModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    Configure Filters
                  </button>
                ) : activeFilterCount > 0 && !syncInfo?.jobs?.lastSyncTime && canManualSyncJobs ? (
                  <button
                    onClick={handleManualSync}
                    disabled={triggeringSyncManually}
                    data-onboarding="sync-jobs-now-button"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {triggeringSyncManually ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Sync Jobs Now
                      </>
                    )}
                  </button>
                ) : activeFilterCount > 0 && !syncInfo?.jobs?.lastSyncTime && !canManualSyncJobs ? (
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg border border-gray-200">
                    Waiting for automatic sync
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => setShowFiltersModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      Adjust Filters
                    </button>
                    <button
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                      Refresh Jobs
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {displayJobs.map((job, index) => (
                  <JobCard
                    key={job._id || job.id}
                    job={job}
                    onClick={(job) => handleJobClick(job, index)}
                    onNotInterested={handleNotInterested}
                    onSkillClick={(skill) => setSearchTerm(skill)}
                    onSaveToggle={refreshSavedJobs}
                    initialSaved={activeTab === 'saved' || savedJobIds.has(job._id)}
                    skipSavedCheck={true}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {activeTab !== 'saved' && totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {/* Always show page 1 */}
                  <button
                    onClick={() => handlePageChange(1)}
                    className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                      currentPage === 1
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    1
                  </button>

                  {/* Ellipsis if gap between 1 and start of range */}
                  {currentPage > 4 && totalPages > 5 && (
                    <span className="w-8 h-8 flex items-center justify-center text-gray-400">...</span>
                  )}

                  {/* Middle pages */}
                  {Array.from({ length: Math.min(3, totalPages - 2) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 2;
                      if (pageNum >= totalPages) return null;
                    } else if (currentPage <= 3) {
                      pageNum = i + 2;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 3 + i;
                    } else {
                      pageNum = currentPage - 1 + i;
                    }

                    // Skip if page 1 or last page (handled separately)
                    if (pageNum <= 1 || pageNum >= totalPages) return null;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-primary-600 text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Ellipsis if gap before last page */}
                  {currentPage < totalPages - 3 && totalPages > 5 && (
                    <span className="w-8 h-8 flex items-center justify-center text-gray-400">...</span>
                  )}

                  {/* Always show last page if more than 1 page */}
                  {totalPages > 1 && (
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === totalPages
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {totalPages}
                    </button>
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters Modal */}
      {showFiltersModal && (
        <JobFiltersModal onClose={() => setShowFiltersModal(false)} />
      )}

      {/* Advanced Search Modal */}
      {showAdvancedSearch && (
        <JobAdvancedSearchModal
          values={advancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
          onApply={(next) => {
            setAdvancedSearch(next);
            setShowAdvancedSearch(false);
          }}
        />
      )}

      {/* Job Details Modal */}
      {selectedJob && (
        <JobDetailsModal
          job={selectedJob}
          proposalStatus={proposalStatuses[selectedJob._id]}
          onClose={() => {
            setSelectedJob(null);
            setSelectedJobIndex(-1);
          }}
          onSaveToggle={refreshSavedJobs}
          currentIndex={selectedJobIndex}
          totalCount={displayJobs.length}
          onNavigate={handleNavigateJob}
        />
      )}
      {/* Undo Toast */}
      {toast && (
        <Toast
          message={toast.message}
          actionLabel={toast.actionLabel}
          onAction={toast.onAction}
          onDismiss={toast.onDismiss}
        />
      )}
    </JobBoardLayout>
  );
}
